'use client';

import { c as _c } from "react/compiler-runtime";
import { useSimilarWinnersByMarket } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type JSX, useRef } from 'react';
import { Skeleton } from '@/components/ui/app-skeleton';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { SimilarWinnerCard } from './card';
const DEFAULT_LIMIT = 6;
const VIEWPORT_PREFETCH_MARGIN = '400px';
// Keep the discovery feed within the high-confidence related tier.
const DISCOVERY_THRESHOLD = 0.45;
const SKELETON_KEYS: readonly string[] = ['s0', 's1', 's2', 's3'];
export interface SimilarWinnersSectionProps {
  /** The source winner's parent market pubkey — the embedding lookup keys on this. */
  readonly marketPubkey: string;
}

/**
 * "Similar winners" feed, shown exclusively on the winner detail page. Reads the
 * source winner's stored vector from the SEPARATE `winner_embedding` space
 * (outcome-aware: title + description + winning outcome label + category) and
 * renders the closest graduated winners, ordered by cosine similarity.
 *
 * Distinct from `<SimilarMarketsSection>` (market detail) and the proposal
 * duplicate-detection nudge. Similar winners are a permanent discovery surface.
 * Cards are presentational leaves (no SDK field hooks) since they render outside
 * any `<Items>` context. No realtime: rows render off the endpoint snapshot.
 */
export function SimilarWinnersSection(t0) {
  const $ = _c(20);
  const {
    marketPubkey
  } = t0;
  const t = useTranslations("markets.similar");
  const sectionRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: VIEWPORT_PREFETCH_MARGIN,
      once: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(sectionRef, t1);
  let t2;
  if ($[1] !== marketPubkey) {
    t2 = {
      pubkey: marketPubkey,
      limit: DEFAULT_LIMIT,
      threshold: DISCOVERY_THRESHOLD
    };
    $[1] = marketPubkey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isIntersecting) {
    t3 = {
      enabled: isIntersecting
    };
    $[3] = isIntersecting;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data,
    isLoading
  } = useSimilarWinnersByMarket(t2, t3);
  if (!isIntersecting || isLoading) {
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <section ref={sectionRef} data-testid="similar-winners-section" data-state="loading" className="grid grid-cols-1 gap-3 md:grid-cols-2">{SKELETON_KEYS.map(_temp)}</section>;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  let t4;
  if ($[6] !== data?.winners) {
    t4 = data?.winners ?? [];
    $[6] = data?.winners;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const winners = t4;
  if (winners.length === 0) {
    return null;
  }
  const t5 = String(winners.length);
  let t6;
  if ($[8] !== t) {
    t6 = t("similarWinners");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <h3 className="text-heading-md text-foreground-primary">{t6}</h3>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== winners) {
    t8 = winners.map(_temp2);
    $[12] = winners;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{t8}</div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t5 || $[17] !== t7 || $[18] !== t9) {
    t10 = <section ref={sectionRef} data-testid="similar-winners-section" data-state="rendered" data-winners={t5} className="flex flex-col gap-3">{t7}{t9}</section>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
function _temp2(winner) {
  return <SimilarWinnerCard key={winner.mint} winner={winner} />;
}
function _temp(key) {
  return <Skeleton key={key} className="h-28 w-full rounded-card" />;
}